import React from "react";
import { Card } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { formatDateToString } from "go-core/components/Formatters/FormatDate";
import { ReactComponent as DangerAlertSVG } from "go-core/images/svg/alert.svg";
import { ReactComponent as WarningAlertSVG } from "go-core/images/svg/warning-alert.svg";
import {
	FILTER_SEPARATOR,
	FILTER_VALUE_SEPARATOR,
	NEW_WAY_TO_ENCODING_FILTER_SIGN,
} from "go-list/core/components/Filter/services";
import EntityStatus from "../../../../../components/Common/EntityStatus/EntityStatus";
import { OrganizationsDashboardApi } from "../../../../../services/Api/types";
import ChurnItem from "./ChurnItem";

interface Props {
	isAdminMode: boolean;
	dashboard: OrganizationsDashboardApi;
	partnerId?: number;
	type: ChurnCardType;
}

type ChurnCardType = "DEFAULT" | "FUTURE";

const ChurnCard = ({ isAdminMode, dashboard, partnerId, type }: Props) => {
	const { t } = useTranslation("admin");
	const adminLink = "/admin/organizations";
	const partnerLink = `/partnership/${partnerId}/organizations`;

	const getFiltersLink = (name: string) => {
		const now = new Date();
		const last30days = new Date(new Date().setDate(now.getDate() - 30));
		const last7days = new Date(new Date().setDate(now.getDate() - 7));
		const next7days = new Date(new Date().setDate(now.getDate() + 7));

		if (name === "cancelled") {
			return btoa(
				unescape(
					encodeURIComponent(
						`${NEW_WAY_TO_ENCODING_FILTER_SIGN}first_paid_at|a${FILTER_SEPARATOR}license_type|e=CANCELLED`
					)
				)
			);
		}
		if (name === "cancelled_in_last30_days") {
			return btoa(
				unescape(
					encodeURIComponent(
						`${NEW_WAY_TO_ENCODING_FILTER_SIGN}first_paid_at|a${FILTER_SEPARATOR}license_type|e=CANCELLED${FILTER_SEPARATOR}cancelled_at|gt=${formatDateToString(
							last30days
						)}`
					)
				)
			);
		}
		if (name === "cancelled_without_reason") {
			return btoa(
				unescape(
					encodeURIComponent(
						`${NEW_WAY_TO_ENCODING_FILTER_SIGN}first_paid_at|a${FILTER_SEPARATOR}license_type|e=CANCELLED${FILTER_SEPARATOR}cancellation_reason|u`
					)
				)
			);
		}
		if (name === "cancelled_in_last30_days_without_reason") {
			return btoa(
				unescape(
					encodeURIComponent(
						`${NEW_WAY_TO_ENCODING_FILTER_SIGN}first_paid_at|a${FILTER_SEPARATOR}license_type|e=CANCELLED${FILTER_SEPARATOR}cancelled_at|gt=${formatDateToString(
							last30days
						)}${FILTER_SEPARATOR}cancellation_reason|u`
					)
				)
			);
		}
		if (name === "license_blocked_in7_days") {
			return btoa(
				unescape(
					encodeURIComponent(
						`${NEW_WAY_TO_ENCODING_FILTER_SIGN}first_paid_at|a${FILTER_SEPARATOR}license_expires_at|bt=${formatDateToString(
							last7days
						)}${FILTER_VALUE_SEPARATOR}${formatDateToString(now)}`
					)
				)
			);
		}
		if (name === "license_expires_in7_days") {
			return btoa(
				unescape(
					encodeURIComponent(
						`${NEW_WAY_TO_ENCODING_FILTER_SIGN}first_paid_at|a${FILTER_SEPARATOR}license_expires_at|bt=${formatDateToString(
							now
						)}${FILTER_VALUE_SEPARATOR}${formatDateToString(next7days)}`
					)
				)
			);
		}
		if (name === "license_active_without_sale_for7_days") {
			return btoa(
				unescape(
					encodeURIComponent(
						`${NEW_WAY_TO_ENCODING_FILTER_SIGN}license_type|e=STANDARD${FILTER_VALUE_SEPARATOR}MANUAL${FILTER_VALUE_SEPARATOR}TRIAL${FILTER_SEPARATOR}first_paid_at|a${FILTER_SEPARATOR}last_sale_at|lt=${formatDateToString(
							last7days
						)}`
					)
				)
			);
		}
		if (name === "license_blocked") {
			return btoa(
				unescape(
					encodeURIComponent(
						`${NEW_WAY_TO_ENCODING_FILTER_SIGN}license_expires_at|lt=${formatDateToString(
							last7days
						)}${FILTER_SEPARATOR}license_type|e=STANDARD${FILTER_SEPARATOR}first_paid_at|a`
					)
				)
			);
		}
	};

	const getAlertType = (type: ChurnCardType): "warning" | "danger" | "" => {
		if (type === "DEFAULT") {
			if (dashboard.cancelled_in_last30_days_without_reason > 0) return "danger";
			if (dashboard.cancelled_without_reason > 0) return "warning";
			return "";
		}
		if (type === "FUTURE") {
			if (dashboard.license_blocked > 0) return "danger";
			if (dashboard.license_blocked_in7_days > 0) return "danger";
			if (dashboard.license_expires_in7_days > 0) return "warning";
			return "";
		}
		return "";
	};

	const getSingleAlertType = (name: string): "warning" | "danger" | "" => {
		if (name === "cancelled_without_reason" && dashboard.cancelled_without_reason > 0) return "warning";
		if (name === "license_expires_in7_days" && dashboard.license_expires_in7_days > 0) return "warning";
		if (name === "cancelled_in_last30_days_without_reason" && dashboard.cancelled_in_last30_days_without_reason > 0)
			return "danger";
		if (name === "license_blocked" && dashboard.license_blocked > 0) return "danger";
		if (name === "license_blocked_in7_days" && dashboard.license_blocked_in7_days > 0) return "danger";
		return "";
	};

	const drawAlertIcon = (name: string) => {
		const warnings = ["cancelled_without_reason", "license_expires_in7_days"];
		const dangers = ["cancelled_in_last30_days_without_reason", "license_blocked", "license_blocked_in7_days"];

		if (warnings.includes(name) && getSingleAlertType(name) === "warning")
			return <WarningAlertSVG className="alert-svg" />;
		if (dangers.includes(name) && getSingleAlertType(name) === "danger")
			return <DangerAlertSVG className="alert-svg" />;
	};

	const drawBoxStatusIcon = (type: ChurnCardType) => {
		const status = getAlertType(type) === "warning" ? "WARNING" : getAlertType(type) === "danger" ? "DISABLED" : "";
		return (
			status && (
				<div className="ms-1">
					<EntityStatus status={status} />
				</div>
			)
		);
	};

	const getLink = (name: string) => {
		const columns = `name,license_type,last_sale_at,last_used_at,company_name,${
			isAdminMode ? "partners" : "partner"
		},cancelled_at,first_paid_at,payment_date_due,not_paid_invoices,cancellation_reason`;
		return `${isAdminMode ? adminLink : partnerLink}?f=${getFiltersLink(name)}&c=${columns}`;
	};

	return (
		<Card className={`churn-card border-${getAlertType(type)}`}>
			<Card.Body>
				<div className="mb-1 d-flex align-items-center">
					<h5 className="mb-0">
						{type === "DEFAULT"
							? t("modules.dashboard.field.churn.title")
							: t("modules.dashboard.field.churn_soon.title")}
					</h5>
					{drawBoxStatusIcon(type)}
				</div>
				<span className="text-muted">
					{type === "DEFAULT"
						? t("modules.dashboard.field.churn.description")
						: t("modules.dashboard.field.churn_soon.description")}
				</span>
				<div className="mt-3">
					{type === "DEFAULT" && (
						<>
							<ChurnItem
								link={getLink("cancelled")}
								icon={drawAlertIcon("cancelled")}
								title={`${t("modules.dashboard.field.cancelled_churns.title")}: ${t(
									"modules.dashboard.field.organizations.title",
									{ count: dashboard.cancelled }
								)}`}
							/>
							<ChurnItem
								link={getLink("cancelled_in_last30_days")}
								icon={drawAlertIcon("cancelled_in_last30_days")}
								title={`${t("modules.dashboard.field.cancelled_in_last_30_days.title")}: ${t(
									"modules.dashboard.field.organizations.title",
									{ count: dashboard.cancelled_in_last30_days }
								)}`}
							/>
							<ChurnItem
								link={getLink("cancelled_without_reason")}
								icon={drawAlertIcon("cancelled_without_reason")}
								title={`${t("modules.dashboard.field.cancelled_without_reason.title")}: ${t(
									"modules.dashboard.field.organizations.title",
									{ count: dashboard.cancelled_without_reason }
								)}`}
							/>
							<ChurnItem
								link={getLink("cancelled_in_last30_days_without_reason")}
								icon={drawAlertIcon("cancelled_in_last30_days_without_reason")}
								title={`${t(
									"modules.dashboard.field.cancelled_in_last_30_days_without_reason.title"
								)}: ${t("modules.dashboard.field.organizations.title", {
									count: dashboard.cancelled_in_last30_days_without_reason,
								})}`}
							/>
						</>
					)}
					{type === "FUTURE" && (
						<>
							<ChurnItem
								link={getLink("license_blocked_in7_days")}
								icon={drawAlertIcon("license_blocked_in7_days")}
								description={t("modules.dashboard.field.license_blocked_in_7_days.description")}
								title={`${t("modules.dashboard.field.license_blocked_in_7_days.title")}: ${t(
									"modules.dashboard.field.organizations.title",
									{ count: dashboard.license_blocked_in7_days }
								)}`}
							/>
							<ChurnItem
								link={getLink("license_expires_in7_days")}
								icon={drawAlertIcon("license_expires_in7_days")}
								description={t("modules.dashboard.field.license_expires_in_7_days.description")}
								title={`${t("modules.dashboard.field.license_expires_in_7_days.title")}: ${t(
									"modules.dashboard.field.organizations.title",
									{ count: dashboard.license_expires_in7_days }
								)}`}
							/>
							<ChurnItem
								link={getLink("license_blocked")}
								icon={drawAlertIcon("license_blocked")}
								title={`${t("modules.dashboard.field.license_blocked.title")}: ${t(
									"modules.dashboard.field.organizations.title",
									{ count: dashboard.license_blocked }
								)}`}
							/>
							<ChurnItem
								link={getLink("license_active_without_sale_for7_days")}
								icon={drawAlertIcon("license_active_without_sale_for7_days")}
								title={`${t(
									"modules.dashboard.field.license_active_without_sale_for_7_days.title"
								)}: ${t("modules.dashboard.field.organizations.title", {
									count: dashboard.license_active_without_sale_for7_days,
								})}`}
							/>
						</>
					)}
				</div>
			</Card.Body>
		</Card>
	);
};

export default ChurnCard;
